import $ from 'jquery';

import { pushSnackbar } from 'FRONTEND/actions/app';
import {
  clear,
  createError,
  createRequest,
  createSuccess,
  cancelError,
  cancelRequest,
  cancelSuccess,
  listError,
  listRequest,
  listSuccess,
  updateError,
  updateRequest,
  updateSuccess,
} from 'FRONTEND/reducers/appointmentSlice';
import {
  goToActorCalendar,
  goToMultipleCalendar,
  pickEvent,
  toggleAppointmentModal,
  updateCurrentDay,
} from 'FRONTEND/reducers/calendarSlice';
import { selectHasRight } from 'FRONTEND/reducers/userSlice';
import { Actor, Appointment } from 'FRONTEND/utils/backendApi/Appointments/appointment.types';
import * as appointmentApi from 'FRONTEND/utils/backendApi/Appointments/appointmentApi';
import * as availabilityApi from 'FRONTEND/utils/backendApi/availabilityApi';
import { EXPERT } from 'FRONTEND/utils/constants';
import i18n from 'FRONTEND/utils/i18n';
import { RIGHTS } from 'FRONTEND/utils/rights';
import { CALENDAR_PATH } from 'FRONTEND/utils/routes';
import { PJAX_MAIN_CONTAINER_SELECTOR } from 'LEGACY/constants';

const appointmentMiddleware = (store) => (next) => async(action) => {
  switch (action.type) {
    case listRequest.toString():
      appointmentApi
        .getAppointments({
          actorId: action.payload?.actorId,
          actorType: action.payload?.actorType,
          startDate: action.payload?.startDate,
          endDate: action.payload?.endDate,
          withTime: action.payload?.withTime,
          isUnrestricted: action.payload?.isUnrestricted,
        })
        .then((appointments) => {
          store.dispatch(
            listSuccess({ appointments: appointments as Appointment[] }),
          );
        })
        .catch((error) => store.dispatch(listError({ error: error.message })));
      return next(action);

    case createRequest.toString(): {
      next(action);
      let { availabilityId, isUnrestricted } = action.payload;
      if (!availabilityId && !isUnrestricted) {
        try {
          const availabilities = await availabilityApi.getAvailabilities({
            actorId: action.payload.requesterId,
            actorType: EXPERT,
            startDate: action.payload.startDate,
            endDate: action.payload.endDate,
          });
          availabilityId = availabilities.find((availability) => {
            const availabilityStartDate = new Date(availability.startDate).toISOString();
            const availabilityEndDate = new Date(availability.endDate).toISOString();
            const slotStartDate = new Date(action.payload.startDate).toISOString();
            const slotEndDate = new Date(action.payload.endDate).toISOString();
            return (
              availabilityStartDate <= slotStartDate &&
              availabilityEndDate >= slotEndDate
            );
          })?.availabilityId;
        } catch (error) {
          store.dispatch(createError({ error: error.message }));
          return;
        }
      }
      const hasAppointmentsCreateAsPatientRight = store.getState().user.rights.includes(RIGHTS.APPOINTMENTS_CREATE_AS_PATIENT);
      const managedIdList = store.getState().expert.managedIdList;
      const managedRequestedIdList = action.payload.requestedIdList.filter(
        (requestedId) => managedIdList.includes(requestedId),
      );
      appointmentApi
        .createAppointment({
          startDate: action.payload.startDate,
          endDate: action.payload.endDate,
          creatorId: action.payload.creatorId,
          patientId: action.payload.patientId,
          requesterId: action.payload.requesterId,
          managedRequestedIdList,
          requestedIdList: action.payload.requestedIdList,
          appointmentType: action.payload.appointmentType,
          appointmentInfo: action.payload.appointmentInfo,
          forceDisablePatientNotif: action.payload.forceDisablePatientNotif,
          availabilityId,
          resourceId: action.payload.resourceId,
          isUnrestricted: action.payload.isUnrestricted,
          tenantIds: action.payload.tenantIds,
          hasPayment: action.payload.hasPayment,
          consultationCodeId: action.payload.consultationCodeId,
          hasAppointmentsCreateAsPatientRight,
        })
        .then((appointment) => {
          store.dispatch(
            createSuccess({ appointment } as { appointment: Appointment }),
          );
        })
        .catch((error) =>
          store.dispatch(createError({ error: error.message })),
        );
      return;
    }

    case createError.toString(): {
      next(action);
      const currentUserId = store.getState().user.id;
      store.dispatch(pushSnackbar({
        message: i18n._('An error occurred. Please retry later.'),
        variant: 'error',
      }));
      store.dispatch(
        goToActorCalendar({
          actorId: currentUserId,
          actorType: EXPERT,
        }),
      );
      break;
    }

    case createSuccess.toString(): {
      const isUnrestrictedMode = store.getState().calendar.isUnrestrictedMode;
      const hasAppointmentCreatePatientRight = selectHasRight(store.getState(), RIGHTS.APPOINTMENTS_CREATE_AS_PATIENT);

      if (store.getState().calendar.isAppointmentModalOpen) {
        store.dispatch(toggleAppointmentModal({ isOpen: false }));
      }

      store.dispatch(pushSnackbar({
        message: i18n._('Appointment created.'),
        variant: 'success',
      }));

      if (isUnrestrictedMode || hasAppointmentCreatePatientRight) {
        return next(action);
      }

      const currentUserId = store.getState().user.id;
      const managedIdList = store.getState().expert.managedIdList;
      const isCurrentUserSecretary = action.payload.appointment.secretary && action.payload.appointment.secretary.id === currentUserId;
      // in case of management by another user, the appointment will contain a secretary object
      const isExpertManagedByCurrentUser = isCurrentUserSecretary && action.payload.appointment.requiredMap && Object.values(action.payload.appointment.requiredMap).some(({ id }) => managedIdList.includes(
        id,
      ));
      store.dispatch(
        updateCurrentDay({ currentDay: action.payload.appointment.startDate }),
      );
      // Assume that there is only one expert in case of a Secretary appointment, since the secretary and patient are already
      // filtered from the requiredMap, which should only contain the main expert (see the formatAppointment in appointmentUtils.ts)
      store.dispatch(
        goToActorCalendar({
          actorId: isExpertManagedByCurrentUser ? (Object.values(action.payload.appointment.requiredMap).find(({ id }) => managedIdList.includes(id)) as Actor).id : currentUserId,
          actorType: EXPERT,
        }),
      );

      const isSelfTakenAppointment = action.payload.appointment.requester?.id === currentUserId;
      if (isExpertManagedByCurrentUser || isSelfTakenAppointment) {
        store.dispatch(
          pickEvent({
            eventId: action.payload.appointment.appointmentId,
            actorId:
                store.getState().booking.selectedExpertId ||
                action.payload.appointment.requester.id,
          }),
        );
      }
      return next(action);
    }

    case updateRequest.toString():
      appointmentApi
        .updateAppointment({
          originalAppointment: action.payload.originalAppointment,
          acceptedIdList: action.payload.acceptedIdList,
          declinedIdList: action.payload.declinedIdList,
          isCancelled: action.payload.isCancelled,
          isForceBooked: action.payload.isForceBooked,
          consultationCodeId: action.payload.consultationCodeId,
        })
        .then((appointment) => {
          store.dispatch(
            updateSuccess({ appointment } as { appointment: Appointment }),
          );
        })
        .catch((error) =>
          store.dispatch(updateError({ error: error.message })),
        );
      return next(action);

    case cancelRequest.toString():
      appointmentApi
        .deleteAppointment({ appointmentId: action.payload.appointmentId })
        .then((appointment) => {
          store.dispatch(
            cancelSuccess({ appointment } as { appointment: Appointment }),
          );
        })
        .catch((error) =>
          store.dispatch(cancelError({ error: error.message })),
        );
      return next(action);

    case updateSuccess.toString():
    case cancelSuccess.toString():
      return next(action);

    case goToMultipleCalendar.toString():
      store.dispatch(
        clear({ actorId: store.getState().calendar.selectedExpertId }),
      );
      return next(action);

    case goToActorCalendar.toString():
      next(action);
      if (window.location.pathname !== CALENDAR_PATH) {
        $.pjax({ url: CALENDAR_PATH, container: PJAX_MAIN_CONTAINER_SELECTOR });
      }
      return;

    default:
      return next(action);
  }
};

export default appointmentMiddleware;
